import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  onSnapshot,
  Unsubscribe,
  Timestamp
} from 'firebase/firestore';
import { db } from './app';
import { VenueAdminInquirySummary } from '../../../../shared/models';

// Convert Firestore timestamp to Date if needed
function convertTimestamp(timestamp: any): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'string') return new Date(timestamp);
  return new Date();
}

// Convert Firestore document to VenueAdminInquirySummary
function convertDocumentToSummary(doc: any): VenueAdminInquirySummary {
  const data = doc.data();
  
  return {
    ...data,
    id: doc.id,
    createdAt: convertTimestamp(data.createdAt),
    lastUpdatedAt: convertTimestamp(data.lastUpdatedAt),
    lastMessageInfo: data.lastMessageInfo ? {
      ...data.lastMessageInfo,
      lastMessageAt: convertTimestamp(data.lastMessageInfo.lastMessageAt)
    } : undefined
  } as VenueAdminInquirySummary;
}

/**
 * Get venue inquiry summaries for a specific venue
 * This replaces the slow Firebase Function call with direct Firestore queries
 */
export async function getVenueInquirySummaries(venueId: string): Promise<VenueAdminInquirySummary[]> {
  const start = performance.now();
  try {
    const q = query(
      collection(db, 'venue_admin_inquiry_summaries'),
      where('venueId', '==', venueId),
      orderBy('lastUpdatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const results = querySnapshot.docs.map(convertDocumentToSummary);
    const end = performance.now();
    console.log(
      `[getVenueInquirySummaries] venueId=${venueId} | count=${results.length} | time=${(end - start).toFixed(2)}ms`
    );
    return results;
  } catch (error) {
    console.error('Error fetching venue inquiry summaries:', error);
    throw error;
  }
}

/**
 * Get venue inquiry summaries with real-time updates
 */
export function subscribeToVenueInquirySummaries(
  venueId: string,
  callback: (summaries: VenueAdminInquirySummary[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, 'venue_admin_inquiry_summaries'),
    where('venueId', '==', venueId),
    orderBy('lastUpdatedAt', 'desc')
  );
  
  return onSnapshot(
    q,
    (querySnapshot) => {
      try {
        const summaries = querySnapshot.docs.map(convertDocumentToSummary);
        callback(summaries);
      } catch (error) {
        console.error('Error processing inquiry summaries:', error);
        if (onError) {
          onError(error as Error);
        }
      }
    },
    (error) => {
      console.error('Error subscribing to inquiry summaries:', error);
      if (onError) {
        onError(error);
      }
    }
  );
}

/**
 * Get inquiry summaries filtered by priority status
 */
export async function getPriorityVenueInquirySummaries(venueId: string): Promise<VenueAdminInquirySummary[]> {
  try {
    const q = query(
      collection(db, 'venue_admin_inquiry_summaries'),
      where('venueId', '==', venueId),
      where('isPriority', '==', true),
      orderBy('lastUpdatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(convertDocumentToSummary);
  } catch (error) {
    console.error('Error fetching priority venue inquiry summaries:', error);
    throw error;
  }
}

/**
 * Get inquiry summaries filtered by availability status
 */
export async function getAvailableVenueInquirySummaries(venueId: string): Promise<VenueAdminInquirySummary[]> {
  try {
    const q = query(
      collection(db, 'venue_admin_inquiry_summaries'),
      where('venueId', '==', venueId),
      where('isUnavailable', '==', false),
      orderBy('lastUpdatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(convertDocumentToSummary);
  } catch (error) {
    console.error('Error fetching available venue inquiry summaries:', error);
    throw error;
  }
}

/**
 * Convert VenueAdminInquirySummary to LimitedAdminInquiry format for backward compatibility
 */
export function convertSummaryToLimitedInquiry(summary: VenueAdminInquirySummary) {
  return {
    inquiryId: summary.inquiryId,
    venueId: summary.venueId,
    matchMakerSessionId: summary.matchMakerSessionId,
    isUnavailable: summary.isUnavailable,
    createdAt: summary.createdAt,
    date: summary.date,
    startTime: summary.startTime,
    endTime: summary.endTime,
    guestCount: summary.guestCount,
    status: summary.sessionStatus,
    isPaymentReceived: summary.isPaymentReceived,
    lastMessageInfo: summary.lastMessageInfo,
    hasReceivedOffer: summary.hasReceivedOffer,
    isPriority: summary.isPriority,
    isStale: summary.isStale,
    sectionId: summary.sectionId,
    sectionName: summary.sectionName
  };
}

/**
 * Get the next best inquiry for a venue to respond to
 * Intelligently prioritizes inquiries based on urgency, recency, and response status
 */
export async function getNextBestInquiry(venueId: string, currentInquiryId?: string): Promise<VenueAdminInquirySummary | null> {
  try {
    const summaries = await getVenueInquirySummaries(venueId);
    
    // Filter out current inquiry and unavailable ones
    const candidates = summaries.filter(summary => 
      summary.inquiryId !== currentInquiryId && 
      !summary.isUnavailable &&
      !summary.isStale &&
      summary.sessionStatus !== 'payment_received' // Don't prioritize already paid inquiries
    );
    
    if (candidates.length === 0) {
      return null;
    }
    
    // Score each inquiry based on priority factors
    const scoredCandidates = candidates.map(summary => {
      let score = 0;
      const now = new Date();
      const createdAt = new Date(summary.createdAt);
      const hoursOld = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
      
      // Priority inquiries get highest score
      if (summary.isPriority) {
        score += 100;
      }
      
      // Recent inquiries (less than 2 hours old) get high score
      if (hoursOld < 2) {
        score += 50;
      } else if (hoursOld < 6) {
        score += 30;
      } else if (hoursOld < 12) {
        score += 10;
      }
      
      // Inquiries without offers yet get priority
      if (!summary.hasReceivedOffer) {
        score += 40;
      }
      
      // Inquiries with recent messages get priority
      if (summary.lastMessageInfo && summary.lastMessageInfo.lastMessageAt) {
        const lastMessageAt = new Date(summary.lastMessageInfo.lastMessageAt);
        const messageHoursOld = (now.getTime() - lastMessageAt.getTime()) / (1000 * 60 * 60);
        
        if (messageHoursOld < 1) {
          score += 30;
        } else if (messageHoursOld < 3) {
          score += 20;
        } else if (messageHoursOld < 6) {
          score += 10;
        }
      }
      
      // Inquiries for today or tomorrow get bonus
      const inquiryDate = new Date(summary.date);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (inquiryDate.toDateString() === today.toDateString()) {
        score += 25; // Today
      } else if (inquiryDate.toDateString() === tomorrow.toDateString()) {
        score += 15; // Tomorrow
      }
      
      // Larger guest counts get slight priority (more revenue potential)
      if (summary.guestCount > 20) {
        score += 5;
      }
      
      return {
        summary,
        score
      };
    });
    
    // Sort by score descending and return the best one
    scoredCandidates.sort((a, b) => b.score - a.score);
    
    return scoredCandidates[0]?.summary || null;
  } catch (error) {
    console.error('Error getting next best inquiry:', error);
    return null;
  }
}

/**
 * Get estimated response time for an inquiry (for UX purposes)
 */
export function getEstimatedResponseTime(summary: VenueAdminInquirySummary): string {
  // Base time factors
  let baseMinutes = 1;
  
  // If they haven't sent an offer yet, might take longer
  if (!summary.hasReceivedOffer) {
    baseMinutes += 0.5;
  }
  
  // If there are recent messages waiting for venue response, might need to read context
  if (summary.lastMessageInfo && summary.lastMessageInfo.waitingForVenueResponse) {
    baseMinutes += 0.5;
  }
  
  // Priority inquiries might need more attention
  if (summary.isPriority) {
    baseMinutes += 0.5;
  }
  
  // Round to nearest 0.5 and cap between 1-2 minutes
  const minutes = Math.max(1, Math.min(2, Math.round(baseMinutes * 2) / 2));
  
  if (minutes === 1) {
    return '1 min';
  } else if (minutes === 1.5) {
    return '1-2 min';
  } else {
    return '2 min';
  }
} 